// ============================================================
// useGridGeometry — グリッド座標計算ユーティリティ
//
// 既存の calendarGeometry.js をラップ/再実装。
// Y軸: 日付+時刻 (下方向)
// X軸: 車両列 (右方向)
// ============================================================

import { GRID_CONFIG } from './types';
import { TIME_SLOTS } from '../../constants/calendar';
import type { TimeSlot } from '../../types';

// TIME_SLOTS は src/constants/calendar.ts の定義が唯一のソース。
// GridCanvas / テストが従来通りここから import できるよう re-export する。
export { TIME_SLOTS };

export const SLOTS_PER_DAY = TIME_SLOTS.length; // 15
export const DAY_HEIGHT_PX = GRID_CONFIG.slotHeight * SLOTS_PER_DAY; // 36 × 15 = 540

/** 日付を "YYYY-MM-DD" の日付部分のみに正規化 */
export const toStartOfDay = (input: Date | string): Date => {
  const d = input instanceof Date ? input : new Date(input);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** rangeStart から targetDate までの日数差 */
export const diffInDays = (base: Date, target: Date): number => {
  const a = toStartOfDay(base).getTime();
  const b = toStartOfDay(target).getTime();
  return Math.floor((b - a) / 86400000);
};

/** hour に対応する TIME_SLOTS のインデックスを返す */
export const findSlotIndex = (hour: number): number => {
  let idx = TIME_SLOTS.findIndex(
    (s) => s.type === 'hour' && s.hour === hour,
  );
  if (idx !== -1) return idx;

  idx = TIME_SLOTS.findIndex(
    (s) => s.type === 'bucket' && s.hours.includes(hour),
  );
  if (idx !== -1) return idx;

  // hour が 9 未満なら morning bucket (0)
  // hour が 22 以上なら night bucket (最後)
  if (hour < 9) return 0;
  return TIME_SLOTS.length - 1;
};

/** スロットの分数と開始時刻を返す */
const resolveSlotMeta = (slot: TimeSlot) => {
  if (slot.type === 'hour') {
    return { minutes: 60, startHour: slot.hour };
  }
  const startHour = slot.hours.length > 0 ? Math.min(...slot.hours) : 0;
  return { minutes: slot.hours.length * 60, startHour };
};

/**
 * 日時 → キャンバス上の絶対 Y 座標を計算（スナップなし）
 * @param rangeStart グリッドの表示開始日
 * @param date       対象の日時
 */
export const dateToAbsoluteY = (rangeStart: Date, date: Date): number => {
  const dayIndex = diffInDays(rangeStart, date);
  const slotIndex = findSlotIndex(date.getHours());
  const slot = TIME_SLOTS[slotIndex];
  const { minutes: slotMinutes, startHour } = resolveSlotMeta(slot);
  const minutesInSlot =
    (date.getHours() - startHour) * 60 +
    date.getMinutes() +
    date.getSeconds() / 60;
  const clamped = Math.max(0, Math.min(slotMinutes, minutesInSlot));
  const fraction = slotMinutes > 0 ? clamped / slotMinutes : 0;
  const yWithinDay = slotIndex * GRID_CONFIG.slotHeight + fraction * GRID_CONFIG.slotHeight;
  return dayIndex * DAY_HEIGHT_PX + yWithinDay;
};

/**
 * 絶対Y座標（ボディピクセル） → Date（スロット開始時刻に丸める）
 * dateToAbsoluteY の逆変換。バケットスロット（早朝/深夜）は先頭時刻に丸まる。
 */
export const absoluteYToDate = (rangeStart: Date, absoluteY: number): Date => {
  const dayIndex = Math.max(0, Math.floor(absoluteY / DAY_HEIGHT_PX));
  const pixelWithinDay = absoluteY - dayIndex * DAY_HEIGHT_PX;
  // 負のY（day 0 のさらに上）では pixelWithinDay が負になり TIME_SLOTS[-1] で
  // クラッシュしていたため 0 にクランプする (2026-07-02 テスト導入時に発見)
  const slotIndex = Math.min(
    Math.max(0, Math.floor(pixelWithinDay / GRID_CONFIG.slotHeight)),
    TIME_SLOTS.length - 1,
  );
  const slot = TIME_SLOTS[slotIndex];
  const hour = slot.type === 'hour' ? slot.hour : (slot.hours[0] ?? 0);
  const dayDate = new Date(rangeStart.getTime() + dayIndex * 86400000);
  dayDate.setHours(hour, 0, 0, 0);
  return dayDate;
};

/**
 * ビューポート座標 → ボディ絶対座標（zoom 対応）
 * ビューポート座標を zoom で割って描画空間に変換し、スクロールも zoom で割る。
 */
export const viewToBody = (
  viewX: number,
  viewY: number,
  scrollX: number,
  scrollY: number,
  zoom: number = 1,
): { bodyX: number; bodyY: number } => {
  const { leftGutterWidth, headerHeight } = GRID_CONFIG;
  const zViewX = viewX / zoom;
  const zViewY = viewY / zoom;
  const zScrollX = scrollX / zoom;
  const zScrollY = scrollY / zoom;
  const bodyX = zViewX - leftGutterWidth + Math.max(0, zScrollX - leftGutterWidth);
  const bodyY = zViewY - headerHeight + Math.max(0, zScrollY - headerHeight);
  return { bodyX, bodyY };
};

/**
 * 車両インデックス → キャンバス上の X 座標（左端）
 */
export const vehicleIndexToX = (index: number): number =>
  index * GRID_CONFIG.columnWidth;

/**
 * 全体キャンバスサイズ（左ガターを除く本体部分）
 */
export const getBodyCanvasSize = (vehicleCount: number, dayCount: number) => ({
  width: vehicleCount * GRID_CONFIG.columnWidth,
  height: dayCount * DAY_HEIGHT_PX,
});
